import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ClarificationSchemasState,
  ClarificationSchema,
  ClarificationSchemaListData,
} from '../../types/clarificationSchemas.types';

const initialState: ClarificationSchemasState = {
  schemas: [],
  selectedSchema: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
    hasMorePages: false,
  },
  search: '',
  sort: {
    field: 'createdAt',
    order: 'desc',
  },
};

const clarificationSchemasSlice = createSlice({
  name: 'clarificationSchemas',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSchemas: (state, action: PayloadAction<ClarificationSchemaListData>) => {
      state.schemas = action.payload.schemas;
      state.pagination = {
        ...state.pagination,
        totalPages: action.payload.totalPages,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loading = false;
      state.error = null;
    },
    setSelectedSchema: (state, action: PayloadAction<ClarificationSchema | null>) => {
      state.selectedSchema = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.pagination.page = 1;
    },
    setSort: (state, action: PayloadAction<{ field: string; order: string }>) => {
      state.sort = action.payload;
    },
    addSchema: (state, action: PayloadAction<ClarificationSchema>) => {
      state.schemas.unshift(action.payload);
      state.pagination.totalRecords += 1;
    },
    updateSchema: (state, action: PayloadAction<ClarificationSchema>) => {
      const index = state.schemas.findIndex((s) => s.id === action.payload.id);
      if (index !== -1) {
        state.schemas[index] = action.payload;
      }
      if (state.selectedSchema?.id === action.payload.id) {
        state.selectedSchema = action.payload;
      }
    },
    removeSchema: (state, action: PayloadAction<string>) => {
      state.schemas = state.schemas.filter((s) => s.id !== action.payload);
      state.pagination.totalRecords -= 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedSchema: (state) => {
      state.selectedSchema = null;
    },
    resetClarificationSchemas: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setSchemas,
  setSelectedSchema,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  addSchema,
  updateSchema,
  removeSchema,
  clearError,
  clearSelectedSchema,
  resetClarificationSchemas,
} = clarificationSchemasSlice.actions;

export default clarificationSchemasSlice.reducer;
